import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface FunnelRow {
  dealStatus: string;
  dealCount: number;
  totalLoanAmountCents: number;
}

export interface StaleDealRow {
  dealId: string;
  dealName: string | null;
  primaryBorrowerName: string | null;
  adviserName: string | null;
  dealStatus: string;
  customStatusName: string | null;
  lenderName: string | null;
  loanAmountCents: number;
  daysSinceLastUpdate: number;
  lastSnapshotDate: string;
}

export interface PipelineReportData {
  totalDealCount: number;
  totalLoanAmountCents: number;
  funnel: FunnelRow[];
  staleDeals: StaleDealRow[];
}

interface State {
  data: PipelineReportData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePipelineReport(
  companyId: string | undefined,
  lookbackDays: number = 30
): State {
  const [data, setData] = useState<PipelineReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    // lookbackDays is a recency window over crm_created_at (deal creation),
    // NOT snapshot_date — it bounds the funnel to deals created in the window
    // so stale/dead NewLeads don't inflate the "in pipeline" count.
    const fn = httpsCallable<
      { companyId: string; lookbackDays?: number },
      PipelineReportData
    >(functions, 'getPipelineReport');
    setLoading(true);
    setError(null);
    fn({ companyId, lookbackDays })
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error('[usePipelineReport] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, lookbackDays, refreshKey]);

  return { data, loading, error, refresh };
}
